import Link from "next/link";
import { ArrowRight } from "lucide-react";
import type { DirectoryListing } from "~/lib/siteglue/directory";
import {
  DIRECTORY_SCORE_AEO,
  DIRECTORY_SCORE_AGENT,
  DIRECTORY_SCORE_CONVERSION,
  DIRECTORY_SCORE_NOT_VERIFIED,
} from "~/lib/siteglue/landing-redesign/copy";

function ScoreCell({
  label,
  value,
  href,
}: {
  label: string;
  value: number | null | undefined;
  href?: string | null;
}) {
  const inner = (
    <>
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-slate-900">
        {typeof value === "number" ? Math.round(value) : DIRECTORY_SCORE_NOT_VERIFIED}
      </p>
    </>
  );
  if (!href) {
    return <div className="rounded-xl bg-slate-50 px-3 py-2 text-center">{inner}</div>;
  }
  return (
    <Link href={href} className="rounded-xl bg-slate-50 px-3 py-2 text-center hover:bg-slate-100">
      {inner}
    </Link>
  );
}

export function DirectoryCard({ listing }: { listing: DirectoryListing }) {
  const verified = listing.lastVerified
    ? listing.lastVerified.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <article className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-3">
        {listing.logoUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={listing.logoUrl}
            alt=""
            className="h-12 w-12 rounded-lg object-cover"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#4f81bd]/10 text-sm font-bold text-[#4f81bd]">
            {listing.businessName.slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold text-slate-900">{listing.businessName}</h2>
          <p className="mt-0.5 text-sm text-slate-600">
            {[listing.industry, listing.location].filter(Boolean).join(" · ") || "SiteGlue Agent"}
          </p>
        </div>
      </div>

      {listing.scores ? (
        <div className="mt-4 grid grid-cols-3 gap-2">
          <ScoreCell
            label={DIRECTORY_SCORE_AGENT}
            value={listing.scores.agentReady}
            href={`/agent-ready/report/${listing.scores.scanId}?tab=agents`}
          />
          <ScoreCell
            label={DIRECTORY_SCORE_CONVERSION}
            value={listing.scores.croReady}
            href={`/agent-ready/report/${listing.scores.scanId}?tab=conversion`}
          />
          <ScoreCell
            label={DIRECTORY_SCORE_AEO}
            value={listing.scores.aeoReady}
            href={`/agent-ready/report/${listing.scores.scanId}?tab=aeo`}
          />
        </div>
      ) : (
        <p className="mt-4 rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-600">
          Not yet verified
        </p>
      )}

      {verified ? (
        <p className="mt-3 text-xs text-slate-500">Last verified {verified}</p>
      ) : null}

      {listing.channels.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-1.5">
          {listing.channels.map((channel) => (
            <li
              key={channel}
              className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700"
            >
              {channel}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-5 flex flex-wrap gap-3">
        <Link
          href={`/directory/${listing.slug}`}
          className="inline-flex items-center gap-1 text-sm font-semibold text-[#4f81bd] hover:underline"
        >
          View listing
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
        {listing.reportHref ? (
          <Link
            href={listing.reportHref}
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-700 hover:underline"
          >
            Readiness report
          </Link>
        ) : null}
        {listing.talkHref ? (
          <Link
            href={listing.talkHref}
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-700 hover:underline"
          >
            Talk to Agent
          </Link>
        ) : null}
      </div>
    </article>
  );
}
